import { AgentStreamHttpError } from "./agentStream";
import type { AgentMemory } from "./agentMemory";
import type { StreamHandlers, StreamOutcome } from "./agentStream";
import type { CallResult, StepOutcome } from "./callHandlers";

// Failures surface to onPlanError in the shape useApiError's toast path understands, so the user
// reads the translated error code instead of a generic "something went wrong".
const shapedPlanError = (status: number, errorCode: string | null, fallback: unknown): unknown =>
  errorCode ? { status, data: { errorCode } } : fallback;

const parseArgs = (argsJson: string): Record<string, unknown> => {
  try {
    const parsed = JSON.parse(argsJson || "{}");
    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};

export type TurnOutcome = StepOutcome | "answered";

export type TurnResult = {
  outcome: TurnOutcome;
  plannerReply: string | null;
  toolContext: string[];
  usedToolNames: string[];
};

export type TurnLoopDeps = {
  planStream: (handlers: StreamHandlers) => Promise<StreamOutcome>;
  executeCall: (callName: string, args: Record<string, unknown>, plannerSummary: string | null) => Promise<CallResult>;
  memory: AgentMemory;
  maxSteps: number;
  setActiveStep: (label: string | null) => void;
  onPlannerText: (accumulated: string) => void;
  onToolCallStart: (name: string) => void;
  onPlanError: (err: unknown) => void;
};

// Drive the plan/execute loop for one user turn over streamed plans. A tool-less round IS the
// answer: its text has already streamed to the turn via onPlannerText, so no separate answer phase
// runs (outcome "answered"). Rounds with tool calls execute every call through the existing gate;
// a call may end the turn early (navigation, a confirmed mutation, or a cancel), delegating the
// SKIPPED balancing to memory — the answer phase then synthesizes from the tool results ("stream").
// The assistant tool-call message enters memory only from a fully assembled stream outcome, so a
// dropped stream can never leave a dangling call.
export const runTurnLoop = async (deps: TurnLoopDeps): Promise<TurnResult> => {
  const { planStream, executeCall, memory, maxSteps, setActiveStep, onPlannerText, onToolCallStart, onPlanError } =
    deps;
  const toolContext: string[] = [];
  const usedToolNames: string[] = [];
  let outcome: TurnOutcome = "stream";
  let plannerReply: string | null = null;

  for (let step = 0; step < maxSteps; step++) {
    setActiveStep("Tenker");
    let planned: StreamOutcome;
    try {
      planned = await planStream({ onDelta: onPlannerText, onToolCallStart });
    } catch (planErr) {
      const shaped = planErr instanceof AgentStreamHttpError
        ? shapedPlanError(planErr.status, planErr.errorCode, planErr)
        : planErr;
      onPlanError(shaped);
      return { outcome: "aborted", plannerReply, toolContext, usedToolNames };
    }
    if (planned.errored && planned.toolCalls.length === 0 && !planned.text.trim()) {
      onPlanError(shapedPlanError(503, planned.errorCode, new Error("plan stream delivered nothing")));
      return { outcome: "aborted", plannerReply, toolContext, usedToolNames };
    }

    const toolCalls = planned.toolCalls;
    if (!toolCalls.length) {
      plannerReply = planned.text.trim() ? planned.text : null;
      if (plannerReply !== null) {
        memory.pushAssistantReply(plannerReply);
      }
      return { outcome: "answered", plannerReply, toolContext, usedToolNames };
    }

    memory.pushAssistantWithToolCalls(planned.text.trim() ? planned.text : null, toolCalls);
    const plannerSummary = planned.text.trim() ? planned.text : null;
    let breakOuter = false;
    for (let i = 0; i < toolCalls.length; i++) {
      const call = toolCalls[i];
      const callResult = await executeCall(call.name, parseArgs(call.arguments), plannerSummary);
      memory.pushToolResult(call.id, callResult.result);
      toolContext.push(`${call.name}: ${JSON.stringify(callResult.result)}`);
      usedToolNames.push(call.name);
      if (callResult.outcome !== null) {
        outcome = callResult.outcome;
      }
      if (callResult.breakOuter) {
        memory.pushSkippedRemainder(toolCalls, i + 1);
        breakOuter = true;
        break;
      }
    }
    if (breakOuter) {
      break;
    }
  }

  return { outcome, plannerReply, toolContext, usedToolNames };
};
